<template>
    <div data-cy="view-container" class="form-container">
        <div class="form-section" data-cy="section-general">
            <div v-if="form_mode == 'init'" class="form-section-header borderline mb-4">
                <div data-cy="company-settings-general-title">{{ $t('company.general.title') }}</div>
            </div>
            <div class="section-body">
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/5 md:w-2/5': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-name-field"
                >
                    <ui-label>{{ $t('company.general.company_name') }}</ui-label>
                    <div class="subText">{{ company.name }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-1/4': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-id-field"
                >
                    <ui-label>{{ $t('company.general.company_id') }}</ui-label>
                    <div class="subText">{{ company.company_id }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-1/3': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-code-field"
                >
                    <ui-label>{{ $t('company.general.company_code') }}</ui-label>
                    <div class="subText">{{ company.company_code }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-1/4': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-type-field"
                >
                    <ui-label>{{ $t('company.general.company_type') }}</ui-label>
                    <div class="subText">{{ captilalizeText(company.company_type) }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/5 md:w-2/5': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-contact-first-name-field"
                >
                    <ui-label>{{ $t('company.general.contact_first_name') }}</ui-label>
                    <div class="subText">{{ company.contact_first_name }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/5 md:w-1/3': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-contact-last-name-field"
                >
                    <ui-label>{{ $t('company.general.contact_last_name') }}</ui-label>
                    <div class="subText">{{ company.contact_last_name }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-1/4': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-salutation-field"
                >
                    <ui-label>{{ $t('company.general.salutation') }}</ui-label>
                    <div class="subText">{{ captilalizeText(company.salutation) }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-1/3': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-national-id-field"
                >
                    <ui-label>{{ $t('company.general.national_id') }}</ui-label>
                    <div class="subText">{{ company.national_id }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-4/5 md:w-3/5': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-mailing-address-field"
                >
                    <ui-label>{{ $t('company.general.mailing_address') }}</ui-label>
                    <div class="subText">{{ company.mailing_address }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/5 md:w-2/5': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-telephone-number-field"
                >
                    <ui-label>{{ $t('company.general.telephone_number') }}</ui-label>
                    <div class="subText">{{ company.telephone_number }}</div>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'lg:w-3/5 md:w-3/5 pr-4': form_mode == 'init', 'w-1/2 px-4': form_mode == 'drawer' }"
                    data-cy="company-settings-general-ip-range"
                    v-if="company.is_ip_address_routing"
                >
                    <ui-label>{{ $t('company.general.ip_range') }}</ui-label>
                    <div class="subText">{{ company.ip_range }}</div>
                </div>
                <div
                    class="input-container mb-2 md:w-1/2"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-email-address-field"
                >
                    <ui-label>{{ $t('company.general.email_address') }}</ui-label>
                    <div class="flex flex-wrap">
                        <div class="subText" v-for="(value, index) in company.email_address" :key="index">
                            {{ value + ',' }}
                        </div>
                    </div>
                </div>
                <div
                    class="input-container mb-2 md:w-1/3"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-active-user-count"
                >
                    <ui-label>{{ $t('company.general.active_users') }}</ui-label>
                    <div class="subText">{{ company.user_count }}</div>
                </div>
                <div
                    class="input-container mb-2 md:w-1/3"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-settings-general-claim-last-imported-date"
                    v-if="company.claim_last_imported_at"
                >
                    <span class="flex justify-between">
                        <ui-label>{{ $t('company.general.claim_last_imported_at') }}</ui-label>
                        <a-tooltip placement="right" :title="$t('company.general.update')" v-if="show_update_button">
                            <SyncOutlined
                                data-cy="drawer-update-date"
                                @click="updateDate"
                                class="hover:text-blue-400"
                                :disabled="processing"
                            />
                        </a-tooltip>
                    </span>
                    <div class="subText">{{ company.claim_last_imported_at }}</div>
                </div>
                <div
                    class="input-container mb-2 md:w-1/3"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-settings-general-cover-last-imported-date"
                    v-if="company.cover_last_imported_at"
                >
                    <span class="flex justify-between">
                        <ui-label>{{ $t('company.general.cover_last_imported_at') }}</ui-label>
                    </span>
                    <div class="subText">{{ company.cover_last_imported_at }}</div>
                </div>
                <div
                    v-if="isCiaEnabled && company.company_type.toLowerCase() == 'insurer'"
                    class="input-container mb-2 md:w-1/2 pr-4"
                    data-cy="company-settings-general-update-authorized-intermediary-id"
                >
                    <span class="flex items-center">
                        <ui-label>{{ $t('company.general.authorized_intermediary_id') }}</ui-label>
                    </span>
                    <div
                        v-if="company.authorized_intermediary_id != null || company.authorized_intermediary_id != ''"
                        class="subText"
                    >
                        {{ company.authorized_intermediary_id }}
                    </div>
                    <div
                        v-if="company.authorized_intermediary_id == null || company.authorized_intermediary_id == ''"
                        class="subText"
                    >
                        {{ $t('company.general.none_set') }}
                    </div>
                </div>
                <div
                    class="input-container mb-2 w-1/2"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-settings-general-deactivation-date"
                    v-if="company.deactivation_date"
                >
                    <span class="flex justify-between">
                        <ui-label>{{ $t('company.general.deactivation_date') }}</ui-label>
                        <a-tooltip
                            placement="right"
                            :title="$t('company.general.update_deactivation_date')"
                            v-if="show_deactivation_button"
                        >
                            <SyncOutlined
                                data-cy="drawer-update-deactivation-date"
                                @click="updateDeactivationDate"
                                class="hover:text-blue-400"
                                :disabled="processing"
                            />
                        </a-tooltip>
                    </span>
                    <div class="subText">{{ company.deactivation_date }}</div>
                </div>
                <div class="w-full" />
                <div
                    class="input-container mb-2 lg:w-1/2"
                    :class="{ 'pr-2': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-logo"
                >
                    <ui-label>{{ $t('company.general.logo') }}</ui-label>
                    <img v-if="company.insurer_logo" :src="company.insurer_logo" alt="avatar" />
                    <div v-else>
                        <div class="subText">{{ $t('company.general.no_logo') }}</div>
                    </div>
                </div>
                <div class="w-full flex mt-8" v-if="isEpicVaultEnabled">
                    <div
                        class="input-container checkbox-container mb-2"
                        :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                    >
                        <div class="cb-viewonly" data-cy="company-field-settings-is-claims-vault-enabled">
                            <ui-checkbox v-model="company.is_module_claimsvault_enabled" :disabled="true">
                                {{ $t('company.general.is_module_claimsvault_enabled') }}
                            </ui-checkbox>
                        </div>
                    </div>
                    <div
                        class="input-container checkbox-container mb-2"
                        :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                    >
                        <div class="cb-viewonly" data-cy="company-field-settings-is-cover-vault-enabled">
                            <ui-checkbox v-model="company.is_module_covervault_enabled" :disabled="true">
                                {{ $t('company.general.is_module_covervault_enabled') }}
                            </ui-checkbox>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="company.is_module_claimsvault_enabled" class="form-section mb-4" data-cy="section-claim-flags">
            <div
                class="form-section-header mb-4"
                :class="{
                    borderline: form_mode == 'init',
                    'border-default-lightgray border-b mx-4': form_mode == 'drawer',
                }"
            >
                <div data-cy="claim-flags">
                    {{ $t('company.general.claims_flags') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-enable-advanced-claims-analytics">
                        <ui-checkbox v-model="company.enable_advanced_claims_analytics" :disabled="true">
                            {{ $t('company.general.enable_advanced_claims_analytics') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-is-hide-claim-description">
                        <ui-checkbox v-model="company.is_hide_claim_description" :disabled="true">
                            {{ $t('company.general.is_hide_claim_description') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-update-upload-date-on-new-claims">
                        <ui-checkbox v-model="company.update_upload_date_on_new_claims" :disabled="true">
                            {{ $t('company.general.update_upload_date_on_new_claims') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-is-enforce-column-count-restriction">
                        <ui-checkbox v-model="company.is_enforce_column_count_restriction" :disabled="true">
                            {{ $t('company.general.is_enforce_column_count_restriction') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-email-rejected-records-on-upload-claims">
                        <ui-checkbox v-model="company.email_rejected_records_on_upload_claims" :disabled="true">
                            {{ $t('company.general.email_rejected_records_on_upload_claims') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-send-import-claim-email">
                        <ui-checkbox v-model="company.send_import_claim_email" :disabled="true">
                            {{ $t('company.general.send_import_claim_email') }}
                        </ui-checkbox>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="company.is_module_covervault_enabled" class="form-section mb-4" data-cy="section-cover-flags">
            <div
                class="form-section-header mb-4"
                :class="{
                    borderline: form_mode == 'init',
                    'border-default-lightgray border-b mx-4': form_mode == 'drawer',
                }"
            >
                <div data-cy="cover-flags">
                    {{ $t('company.general.cover_flags') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container mb-2 lg:w-1/2"
                    :class="{ 'pr-2': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-max-imediate-cover-processing-limit-field"
                >
                    <ui-label>{{ $t('company.general.max_immediate_cover_processing_limit') }}</ui-label>
                    <div class="subText">{{ company.max_immediate_cover_processing_limit }}</div>
                </div>
                <div
                    v-if="isCiaEnabled"
                    class="input-container mb-2 w-1/2"
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    data-cy="company-responsible-user-field"
                >
                    <ui-label>{{ $t('company.general.responsible_user') }}</ui-label>
                    <div class="subText">{{ responsibleUser }}</div>
                    <p class="text-help">
                        {{ $t('company.general.help.responsible_user') }}
                    </p>
                </div>
                <div
                    v-if="isCiaEnabled && company.company_type.toLowerCase() == 'insurer'"
                    class="input-container mb-6 w-full"
                    :class="{ 'pr-2': form_mode == 'init', 'mb-2 px-4': form_mode == 'drawer' }"
                    data-cy="company-cover-document-filename"
                >
                    <ui-label>{{ $t('company.general.existing_cover_document_filename') }}</ui-label>
                    <div class="subText">
                        {{ coverDocumentTemplateValue }}
                    </div>
                    <p class="text-help">
                        {{ $t('company.general.help.cover_document_filename') }}
                    </p>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-enable-advanced-covers-analytics">
                        <ui-checkbox v-model="company.enable_advanced_cover_analytics" :disabled="true">
                            {{ $t('company.general.enable_advanced_cover_analytics') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-send-import-cover-email">
                        <ui-checkbox v-model="company.send_import_cover_email" :disabled="true">
                            {{ $t('company.general.send_import_cover_email') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'xl:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-include-rejected-covers-report-email">
                        <ui-checkbox v-model="company.email_rejected_records_on_upload_covers" :disabled="true">
                            {{ $t('company.general.email_rejected_records_on_upload_covers') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-allow-full-association-cover-search">
                        <ui-checkbox v-model="company.allow_full_association_cover_search" :disabled="true">
                            {{ $t('company.general.allow_full_association_cover_search') }}
                        </ui-checkbox>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-section mb-12" data-cy="section-company-flags">
            <div
                class="form-section-header mb-4"
                :class="{
                    borderline: form_mode == 'init',
                    'border-default-lightgray border-b mx-4': form_mode == 'drawer',
                }"
            >
                <div data-cy="company-flags">
                    {{ $t('company.general.title_flags') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-is-active">
                        <ui-checkbox v-model="company.is_active" :disabled="true">
                            {{ $t('company.general.is_active') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-is-no-lock-out">
                        <ui-checkbox v-model="company.is_no_lock_out" :disabled="true">
                            {{ $t('company.general.is_no_lock_out') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-is-api-access">
                        <ui-checkbox v-model="company.is_api_access" :disabled="true">
                            {{ $t('company.general.is_api_access') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-allow-multi-ip-sessions">
                        <ui-checkbox v-model="company.allow_multi_ip_sessions" :disabled="true">
                            {{ $t('company.general.allow_multi_ip_sessions') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-replace-tp">
                        <ui-checkbox v-model="company.is_replace_tp" :disabled="true">
                            {{ $t('company.general.is_replace_tp') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-is-data-provider">
                        <ui-checkbox v-model="company.is_data_provider" :disabled="true">
                            {{ $t('company.general.is_data_provider') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-is-enforce-filename-restriction">
                        <ui-checkbox v-model="company.is_enforce_filename_restriction" :disabled="true">
                            {{ $t('company.general.is_enforce_filename_restriction') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-is-tfa-enabled">
                        <ui-checkbox v-model="company.is_tfa_enabled" :disabled="true">
                            {{ $t('company.general.is_tfa_enabled') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3 pr-4': form_mode == 'init', 'lg:w-1/2 px-4': form_mode == 'drawer' }"
                >
                    <div class="cb-viewonly" data-cy="company-field-settings-multi-association-link">
                        <ui-checkbox v-model="company.is_multi_association_link" :disabled="true">
                            {{ $t('company.general.is_multi_association_link') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    class="input-container mb-2 w-full"
                    data-cy="association-company-field-settings-linked-associations"
                    v-if="company.is_multi_association_link"
                >
                    <ui-label>{{ $t('company.general.link_association_ids') }}</ui-label>
                    <div class="subText">{{ company.linked_associations }}</div>
                </div>
                <div
                    :class="{ 'pr-4': form_mode == 'init', 'px-4': form_mode == 'drawer' }"
                    class="input-container mb-2 w-full"
                    data-cy="association-company-field-settings-linked-companies"
                    v-if="company.is_multi_association_link"
                >
                    <ui-label>{{ $t('company.general.related_companies') }}</ui-label>
                    <div class="subText">{{ company.linked_companies }}</div>
                </div>
            </div>
        </div>
        <div class="drawer-footer" v-if="form_mode == 'drawer'">
            <slot name="actions"></slot>

            <a-button data-cy="drawer-cancel-button" @click="onDrawerClose" class="btn-cancel">
                {{ $t('company.general.buttons.cancel') }}
            </a-button>
        </div>
    </div>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiLabel from '@/components/ui/Label.vue';
import { SyncOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        UiCheckbox,
        UiLabel,
        SyncOutlined,
    },
    props: {
        company: Object,
        processing: Boolean,
        form_mode: {
            type: String,
            default: 'init',
        },
        show_update_button: {
            type: Boolean,
            default: true,
        },
        show_deactivation_button: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        isEpicVaultEnabled() {
            return this.$store.getters['system/isEpicvaultEnabled'];
        },
        isCiaEnabled() {
            return this.$store.state.user.modules.cia;
        },
        coverDocumentTemplateValue() {
            return this.company.cover_document_filename !== '' &&
                this.company.cover_document_filename !== null &&
                this.company.cover_document_filename !== undefined
                ? this.company.cover_document_filename
                : this.$t('company.general.no_cover_document_template');
        },
        responsibleUser() {
            return this.company.cover_document_filename !== '' &&
                this.company.cover_document_filename !== null &&
                this.company.cover_document_filename !== undefined &&
                this.company.responsible_user.length > 0
                ? this.company.responsible_user
                : this.$t('company.general.none_set');
        },
    },
    methods: {
        captilalizeText(text) {
            if (text !== '' && text !== undefined && text !== null) {
                return text.replace(/^\w/, (c) => c.toUpperCase());
            }

            return '';
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
        updateDate() {
            this.$emit('updateDate', this.company.id);
        },
        updateDeactivationDate() {
            this.$emit('updateDeactivationDate', this.company.id);
        },
    },
};
</script>
